import {ApplicationRef} from "@angular/core"
import {AccessoryModel} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/accessories"
import {
    applyCameraParams,
    applyDefaultCamera,
    getCameraParams,
    setCameraToCurrent,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/camera"
import {getTemplateLegacyId} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/asset-id"
import {getSelectedElementType} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/element-type"
import {selectMaterialInput} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/material-slots"
import {
    getHandleSettings,
    removeHandle,
    setHandle,
    setHandleCount,
    supportsHandles,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/handles"
import {FabricStackLayerModel, type LayerModel} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/layer-model"
import {getDimensions} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/mattress"
import {getParameterStorage, getTemplateOrigin} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/parameter-storage"
import {
    getTextureFiltering,
    getTextureQuality,
    setTextureFiltering,
    setTextureQuality,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/scene-properties"
import {getUsedElements} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/usedElements"
import {
    setColor,
    setColorWithSubstrate,
    setHandleColorWithSubstrate,
    setHandleMaterial,
    setHandleMaterialHorizontalOffset,
    setHandleMaterialRotation,
    setHandleMaterialVerticalOffset,
    setMaterial,
    setMaterialHorizontalOffset,
    setMaterialRotation,
    setMaterialVerticalOffset,
    setQuilt,
    setQuiltHorizontalOffset,
    setQuiltStrength,
    setQuiltVerticalOffset,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/materials"
import {
    BdApi,
    ConfiguratorState,
    HandleCounts,
    NumberValue,
    SideForHandle,
    TemplateDataForSaving,
    TextureQuality,
    UsedElements,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {ConfiguratorExtension, ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {TemplateGraph, TemplateReference} from "@cm/template-nodes"
import {defer, exhaustMap, Subject, takeUntil, firstValueFrom, filter, timeout, EMPTY} from "rxjs"
export class BdApiExtension implements ConfiguratorExtension, BdApi {
    private unsubscribe = new Subject<void>()
    context?: ExtensionContext
    private stateChangeCallbacks: Array<(configuratorState: ConfiguratorState) => void> = []
    private syncing: boolean = false
    private layerModel: LayerModel | undefined
    private accessoryModel: AccessoryModel | undefined
    private currentState: ConfiguratorState = {
        dimensions: undefined,
        selectedElement: undefined,
        supportsHandles: false,
        accessoryGroupData: [],
        textureQuality: "Standard",
        textureFiltering: false,
    }

    destructor(): void {
        this.unsubscribe.next()
        this.unsubscribe.complete()
        this.stateChangeCallbacks = []
        this.layerModel = undefined
        this.accessoryModel = undefined
        this.context = undefined
    }

    registerStateChangeCallback(callback: (configuratorState: ConfiguratorState) => void): () => void {
        this.stateChangeCallbacks.push(callback)
        this.publishState()

        return () => {
            const index = this.stateChangeCallbacks.indexOf(callback)
            if (index > -1) {
                this.stateChangeCallbacks.splice(index, 1)
            }
        }
    }

    setExtensionContext = (context: ExtensionContext): void => {
        this.context = context

        const sceneManagerService = this.getContext().sceneManagerService

        sceneManagerService.selectedRootNodeParts$.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
            this.publishState()
        })
        //TODO: This fires too often -> with syncAndPublish it works, but it has the slightly ugly this.syncing inside
        // sceneManagerService.descriptorList$.pipe(takeUntil(this.unsubscribe)).subscribe(async () => {
        //     //if (sceneManagerService.$ready()) this.publishState()
        //     this.syncAndPublish()
        // })

        sceneManagerService.descriptorList$
            .pipe(
                exhaustMap(() => defer(() => sceneManagerService.sync(false))),
                takeUntil(this.unsubscribe),
            )
            .subscribe(() => {
                this.publishState()
            })
    }

    contextInitialized = (): boolean => {
        return this.context !== undefined
    }

    private getContext(): ExtensionContext {
        if (!this.context) throw new Error("Extension context is not set")
        return this.context
    }

    private getLayerModel(): LayerModel {
        if (!this.layerModel) throw new Error("Layer model is not initialized")
        return this.layerModel
    }

    private getAccessoryModel(): AccessoryModel {
        if (!this.accessoryModel) throw new Error("Accessory model is not initialized")
        return this.accessoryModel
    }

    private getTemplateGraph(): TemplateGraph {
        const {sceneManagerService} = this.getContext()
        const templateGraph = sceneManagerService.$templateGraph()
        if (!templateGraph) throw new Error("Template graph is not available")
        return templateGraph
    }

    private getDimensions(): NumberValue[] {
        return getDimensions(this.getContext())
    }

    setMaterial = async (inputId: string, materialArticleId: string) => {
        await setMaterial(inputId, materialArticleId, this.getContext())
    }

    setHandleMaterial = async (inputId: string, materialArticleId: string): Promise<void> => {
        await setHandleMaterial(inputId, materialArticleId, this.getContext())
    }

    setHandleColorWithSubstrate = (inputId: string, color: string, substrateId: number): void => {
        setHandleColorWithSubstrate(inputId, color, substrateId, this.getContext())
    }

    setHandleMaterialHorizontalOffset = (inputId: string, offset: number): void => {
        setHandleMaterialHorizontalOffset(inputId, offset, this.getContext())
    }

    setHandleMaterialVerticalOffset = (inputId: string, offset: number): void => {
        setHandleMaterialVerticalOffset(inputId, offset, this.getContext())
    }

    setHandleMaterialRotation = (inputId: string, rotation: number): void => {
        setHandleMaterialRotation(inputId, rotation, this.getContext())
    }

    setQuilt = async (inputId: string, quiltId: string | undefined) => {
        await setQuilt(inputId, quiltId, this.getContext())
    }

    setQuiltStrength = (inputId: string, strength: number) => {
        setQuiltStrength(inputId, strength, this.getContext())
    }

    setMaterialHorizontalOffset = (inputId: string, offset: number) => {
        setMaterialHorizontalOffset(inputId, offset, this.getContext())
    }

    setMaterialVerticalOffset = (inputId: string, offset: number) => {
        setMaterialVerticalOffset(inputId, offset, this.getContext())
    }

    setMaterialRotation = (inputId: string, rotation: number) => {
        setMaterialRotation(inputId, rotation, this.getContext())
    }

    setQuiltHorizontalOffset = (inputId: string, offset: number) => {
        setQuiltHorizontalOffset(inputId, offset, this.getContext())
    }

    setQuiltVerticalOffset = (inputId: string, offset: number) => {
        setQuiltVerticalOffset(inputId, offset, this.getContext())
    }

    setColor = (inputId: string, color: string) => {
        setColor(inputId, color, this.getContext())
    }

    setColorWithSubstrate = (inputId: string, color: string, substrateId: number) => {
        setColorWithSubstrate(inputId, color, substrateId, this.getContext())
    }

    setHandleCount = (side: SideForHandle, count: HandleCounts) => {
        const handleSettings = this.currentState.handleSettings
        const currentCount: HandleCounts = handleSettings?.[side] ?? 0
        setHandleCount(side, count, currentCount, handleSettings?.reference, this.getContext())
    }

    setHandle = async (handleId: string): Promise<void> => {
        await setHandle(handleId, this.getContext())
    }

    removeHandle = (): void => {
        removeHandle(this.getContext())
    }

    persistCurrentCameraView = (): void => {
        setCameraToCurrent(this.getContext())
    }

    clearSelection = (): void => {
        this.getContext().sceneManagerService.selectNode(undefined)
    }

    selectMaterialInput = (inputId: string): void => {
        // Only for a directly selected puller or handle, e.g. not for the puller shown inside a selected zipper.
        const selectedType = this.currentState.selectedElement?.type
        if (selectedType !== "Puller" && selectedType !== "Handle") return
        selectMaterialInput(inputId, this.getContext())
    }

    setTextureQuality = (quality: TextureQuality): void => {
        setTextureQuality(this.getContext(), quality)
        //Changing scene properties does not necessarily change the descriptors, so the state is published here
        this.publishState()
    }

    setTextureFiltering = (enabled: boolean): void => {
        setTextureFiltering(this.getContext(), enabled)
        //Changing scene properties does not necessarily change the descriptors, so the state is published here
        this.publishState()
    }

    sync = async (waitForOptionalTasks = false): Promise<void> => {
        const {threeSceneManagerService, injector} = this.getContext()
        await threeSceneManagerService.sync(waitForOptionalTasks)
        injector.get(ApplicationRef).tick()
        threeSceneManagerService.redraw({immediate: true})
        // await new Promise((resolve) => setTimeout(resolve, 10)) // TODO: see sync-debug.md for a proper fix, but this needs discussion first
        const TAA_FRAMES_TO_AWAIT = 60
        const SETTLE_TIMEOUT_MS = 5000
        await firstValueFrom(
            threeSceneManagerService.requestedRedraw$.pipe(
                filter(({taaFrame}) => taaFrame !== null && taaFrame >= TAA_FRAMES_TO_AWAIT),
                timeout({first: SETTLE_TIMEOUT_MS, with: () => EMPTY}),
                takeUntil(this.unsubscribe),
            ),
        )
    }

    getCurrentCameraParams = (): string => {
        return getCameraParams(this.getContext())
    }

    setCamera = (params: string): void => {
        applyCameraParams(params, this.getContext())
    }

    loadDefaultCamera = async (): Promise<void> => {
        await applyDefaultCamera(this.getContext())
    }

    setNumberInput(id: string, value: number): void {
        this.getContext().sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key} = getParameterStorage(templateGraph, id)
            storingInstance.parameters.parameters.updateParameters({[key]: value})
        })
    }

    private printDescriptors(): void {
        const logger = this.getContext().logger
        const descriptors = this.getContext().sceneManagerService.$descriptorList()

        const inputs = descriptors.filter((d) => d.props.type === "input")
        const outputs = descriptors.filter((d) => d.props.type === "output")

        logger.info("DESCRIPTORS")
        logger.info("=== Inputs ===")
        for (const d of inputs) {
            logger.info(d.props.id)
        }
        logger.info("=== Outputs ===")
        for (const d of outputs) {
            logger.info(d.props.id)
        }
    }

    //TODO: Due to the subscription to the $descriptorList, this might not be needed anymore. Keep for a while though...
    private async syncAndPublish() {
        if (this.syncing) return
        const {sceneManagerService} = this.getContext()
        this.syncing = true
        await sceneManagerService.sync(false)
        this.syncing = false
        this.publishState()
    }

    removeAccessory(seamId: string, displayIndex: number): void {
        this.getAccessoryModel().removeAccessory(seamId, displayIndex)
    }

    async assignTemplateToAccessory(templateInputId: string, templateId: string): Promise<void> {
        const templateLegacyId = await getTemplateLegacyId(templateId, this.getContext())
        const {value, setOnRoot} = getTemplateOrigin(templateInputId, this.getContext())
        if (value && setOnRoot && value instanceof TemplateReference) {
            this.getContext().sceneManagerService.modifyTemplateGraph(() => value.updateParameters({templateId: templateLegacyId}))
        } else {
            this.getContext().sceneManagerService.modifyTemplateGraph((templateGraph) => {
                const {storingInstance, key} = getParameterStorage(templateGraph, templateInputId)
                storingInstance.parameters.parameters.updateParameters({
                    [key]: new TemplateReference({parameters: {templateId: templateLegacyId, templateRevisionId: null}}),
                })
            })
        }
    }

    removePuller(templateInputId: string): void {
        //A removed puller must not stay selected, the state cannot be built for it anymore.
        const selectedElement = this.currentState.selectedElement
        if (selectedElement?.type === "Puller" && selectedElement.templateInputId === templateInputId) this.clearSelection()

        this.getContext().sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key} = getParameterStorage(templateGraph, templateInputId)
            const params = {...storingInstance.parameters.parameters.parameters}
            delete params[key]
            storingInstance.parameters.parameters.replaceParameters(params)
        })
    }

    async addAccessory(seamId: string, templateId: string): Promise<void> {
        await this.getAccessoryModel().addAccessory(seamId, templateId)
    }

    moveAccessory(seamId: string, sourceDisplayIndex: number, targetDisplayIndex: number) {
        this.getAccessoryModel().moveAccessory(seamId, sourceDisplayIndex, targetDisplayIndex)
    }

    addLayer = (insertAtIndex: number) => {
        this.getLayerModel().addLayer(insertAtIndex)
    }

    removeLayer = (layerIndex: number) => {
        const removableCount = this.getLayerModel()
            .getLayers()
            .filter((l) => l.canRemove).length
        this.getLayerModel().removeLayer(layerIndex)
        if (removableCount <= 1) {
            removeHandle(this.getContext())
        }
    }

    moveLayer = (sourceIndex: number, targetIndex: number) => {
        this.getLayerModel().moveLayer(sourceIndex, targetIndex)
    }

    canMoveLayer = (sourceIndex: number, targetIndex: number): boolean => {
        return this.getLayerModel().canMove(sourceIndex, targetIndex)
    }

    setLayerHeight = (_layerId: string, _height: number) => {
        throw new Error("setLayerHeight is not available — MattressFabricStackInterface was removed")
    }

    canUndo = (): boolean => {
        if (!this.context) return false
        return this.context.sceneManagerService.$canUndo()
    }

    canRedo = (): boolean => {
        if (!this.context) return false
        return this.context.sceneManagerService.$canRedo()
    }

    undo = () => {
        const sceneManagerService = this.getContext().sceneManagerService
        sceneManagerService.undo()
    }

    redo = () => {
        const sceneManagerService = this.getContext().sceneManagerService
        sceneManagerService.redo()
    }

    publishState = async () => {
        //When syncing, there are cases the configurator sends additional events that make the UI flicker, those must be ignored, a publish is invoked afterwards.
        if (this.syncing) return
        if (this.stateChangeCallbacks.length === 0) return
        if (!this.context) return

        this.layerModel = new FabricStackLayerModel(this.getContext()) //can be reused for layer manipulation, subsequent publishState updates it
        this.accessoryModel = new AccessoryModel(this.getContext()) //can be reused for accessory manipulation, subsequent publishState updates it
        const descriptors = this.getContext().sceneManagerService.$descriptorList()
        this.currentState = {
            dimensions: this.getDimensions(),
            layers: this.layerModel.getLayers(),
            supportsHandles: supportsHandles(descriptors),
            handleSettings: await getHandleSettings(descriptors, this.getContext()),
            selectedElement: await getSelectedElementType(this.getContext(), this.layerModel.getLayers()),
            accessoryGroupData: this.accessoryModel.getAccessoryGroups(),
            textureQuality: getTextureQuality(this.getTemplateGraph()),
            textureFiltering: getTextureFiltering(this.getTemplateGraph()),
        }

        this.stateChangeCallbacks.forEach((callback) => {
            callback(this.currentState)
        })
    }

    getUsedElements = async (): Promise<UsedElements> => {
        return getUsedElements(this.getTemplateGraph(), this.getContext())
    }

    getDataForSaving(): TemplateDataForSaving {
        const {sceneManagerService} = this.getContext()

        this.clearSelection()
        this.persistCurrentCameraView()

        const templateRevisionId = sceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw new Error("No template revision ID available")

        const result = {
            templateRevisionId,
            serializedTemplateGraph: sceneManagerService.$templateGraph().serialize(),
        }
        return result
    }
}
